import React from 'react';
import { Calendar, AlertTriangle, Plus, Download, Upload, Sparkles, Sun, Moon, DollarSign } from 'lucide-react';
import { LivePresenceBadge } from './LivePresenceBadge.tsx';
import { UserPresence } from '../../services/realtime.ts';

export type NavTabId = 'calendar' | 'analysis' | 'finances';

interface NavbarProps {
  activeTab: NavTabId;
  onTabChange: (tab: NavTabId) => void;
  onAddEvent: () => void;
  onOpenTemplates: () => void;
  onExportJSON: () => void;
  onImportClick: () => void;
  hardViolationsCount: number;
  softViolationsCount: number;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  currentUser: UserPresence;
  connectedUsers: UserPresence[];
  isConnected: boolean;
  onUpdateName: (name: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  activeTab,
  onTabChange,
  onAddEvent,
  onOpenTemplates,
  onExportJSON,
  onImportClick,
  hardViolationsCount,
  softViolationsCount,
  theme,
  onToggleTheme,
  currentUser,
  connectedUsers,
  isConnected,
  onUpdateName,
}) => {
  return (
    <header className="navbar-container">
      <div className="navbar">
        <div className="nav-brand">
          <div className="brand-logo-shell">
            <div className="brand-logo">ESN</div>
          </div>
          <div>
            <div className="brand-title">O-Weeks Planner</div>
            <div className="brand-subtitle">28.09 – 12.10.2026</div>
          </div>
        </div>

        <div className="nav-tabs">
          <button
            className={`nav-tab ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => onTabChange('calendar')}
          >
            <Calendar size={18} strokeWidth={1.8} />
            <span>Calendar</span>
          </button>

          <button
            className={`nav-tab ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => onTabChange('analysis')}
          >
            <AlertTriangle size={18} strokeWidth={1.8} />
            <span>Analysis</span>
            {hardViolationsCount > 0 && (
              <span className="nav-badge hard">
                {hardViolationsCount}
              </span>
            )}
            {softViolationsCount > 0 && hardViolationsCount === 0 && (
              <span className="nav-badge soft">
                {softViolationsCount}
              </span>
            )}
          </button>

          <button
            className={`nav-tab ${activeTab === 'finances' ? 'active' : ''}`}
            onClick={() => onTabChange('finances')}
          >
            <DollarSign size={18} strokeWidth={1.8} />
            <span>Finances</span>
          </button>
        </div>


        <LivePresenceBadge
          currentUser={currentUser}
          connectedUsers={connectedUsers}
          isConnected={isConnected}
          onUpdateName={onUpdateName}
        />

        <div className="nav-actions">
          <button
            className="icon-btn theme-toggle-btn"
            onClick={onToggleTheme}
            title={theme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
          >
            {theme === 'dark' ? <Sun size={18} strokeWidth={1.8} /> : <Moon size={18} strokeWidth={1.8} />}
          </button>

          <button className="btn btn-secondary btn-sm" onClick={onImportClick} title="Import JSON schedule">
            <span className="btn-icon-wrapper">
              <Upload size={16} strokeWidth={1.8} />
            </span>
            <span>Import</span>
          </button>

          <button className="btn btn-secondary btn-sm" onClick={onExportJSON} title="Export schedule to JSON">
            <span className="btn-icon-wrapper">
              <Download size={16} strokeWidth={1.8} />
            </span>
            <span>Export</span>
          </button>

          <button className="btn btn-secondary btn-sm" onClick={onOpenTemplates} title="Add event from template">
            <span className="btn-icon-wrapper accent-purple">
              <Sparkles size={16} strokeWidth={1.8} />
            </span>
            <span>Templates</span>
          </button>

          <button className="btn btn-primary" onClick={onAddEvent}>
            <span>New Event</span>
            <span className="btn-icon-wrapper btn-icon-primary">
              <Plus size={18} strokeWidth={2.5} />
            </span>
          </button>
        </div>
      </div>
    </header>
  );
};
